/**
 * PWA-2 — service-worker registration + update prompt.
 *
 * The app-shell worker precaches the built JS/CSS/HTML, so a deployed build
 * keeps serving from cache until a new worker takes over. Rather than
 * reloading the moment that happens (`registerType: 'autoUpdate'`, which
 * would throw away a half-filled invoice or order form), this asks.
 *
 * WHY THE PROMPT STOPPED APPEARING. `useRegisterSW` checks for a new worker
 * exactly once, when it registers — that is, on page load. Nothing re-checked
 * afterwards, so a tab left open all day and an installed PWA resumed from
 * the background never learned a new build existed: no prompt, and the old
 * bundle kept being served from precache. The two symptoms were one cause —
 * "the new tab isn't there" and "the update notice never comes" are the same
 * session never finding out.
 *
 * So it now re-checks on a timer, when the app regains focus, and when the
 * connection comes back.
 *
 * Rendered once, above the router, so it survives navigation.
 */

import { Alert, Button, Snackbar, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRegisterSW } from 'virtual:pwa-register/react';

import { useSyncQueue } from '../services/syncQueue';

/** How often a running app asks whether a new build has landed. */
export const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000;

/**
 * How long "Later" quiets the prompt.
 *
 * Dismissing used to hide it until the NEXT deploy produced a fresh
 * `needRefresh`, which with no periodic check meant "never again this
 * session". Later must mean later, not never.
 */
export const REMIND_AFTER_MS = 60 * 60 * 1000;

export function PwaUpdatePrompt() {
  const { t } = useTranslation('common');
  const { pending, failed, syncing } = useSyncQueue();
  const [snoozedUntil, setSnoozedUntil] = useState<number | null>(null);
  const [, forceTick] = useState(0);
  const registration = useRef<ServiceWorkerRegistration | null>(null);

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, reg) {
      if (!reg) return;
      registration.current = reg;
    },
    onRegisterError(error) {
      // Never surface this: a failed SW registration degrades to a plain
      // online web app, which is exactly the pre-PWA-2 behaviour.
      console.warn('[pwa] service worker registration failed', error);
    },
  });

  const checkForUpdate = useCallback(() => {
    // `update()` rejects while offline or when the worker is already
    // updating; neither is worth telling anybody about.
    void registration.current?.update().catch(() => undefined);
  }, []);

  useEffect(() => {
    const id = window.setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') checkForUpdate();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', checkForUpdate);
    window.addEventListener('online', checkForUpdate);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', checkForUpdate);
      window.removeEventListener('online', checkForUpdate);
    };
  }, [checkForUpdate]);

  // Bring a snoozed prompt back without needing another deploy.
  useEffect(() => {
    if (snoozedUntil === null) return;
    const id = window.setTimeout(
      () => {
        setSnoozedUntil(null);
        forceTick((n) => n + 1);
      },
      Math.max(0, snoozedUntil - Date.now()),
    );
    return () => window.clearTimeout(id);
  }, [snoozedUntil]);

  const snoozed = snoozedUntil !== null && Date.now() < snoozedUntil;
  const open = needRefresh && !snoozed;

  // Work that a reload would lose or delay. The update is user-initiated, so
  // nothing is discarded behind their back — but "reload now" reads very
  // differently when six check-outs are still queued on a phone in a
  // basement, and they deserve to be told rather than surprised.
  const unsentWork = pending + failed;

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{
        zIndex: (theme) => theme.zIndex.snackbar,
        bottom: 'calc(24px + var(--worker-navigation-offset, 0px))',
      }}
    >
      <Alert
        severity="info"
        variant="filled"
        action={
          <Stack direction="row" spacing={1}>
            <Button
              color="inherit"
              size="small"
              onClick={() => setSnoozedUntil(Date.now() + REMIND_AFTER_MS)}
            >
              {t('pwa.later')}
            </Button>
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              disabled={syncing}
              onClick={() => {
                setNeedRefresh(false);
                void updateServiceWorker(true);
              }}
            >
              {t('pwa.update')}
            </Button>
          </Stack>
        }
      >
        <Stack spacing={0.5}>
          <span>{t('pwa.updateAvailable')}</span>
          {unsentWork > 0 && (
            <Typography variant="caption">{t('pwa.unsentWork', { count: unsentWork })}</Typography>
          )}
        </Stack>
      </Alert>
    </Snackbar>
  );
}
